import React from 'react';
import type { ChatWidgetAPI, ChatWidgetProps, MountAPI } from './types';

export function mount(
  options: { container?: HTMLElement; props?: ChatWidgetProps } = {}
): MountAPI {
  const { container, props = {} } = options;
  const target = container || document.createElement('div');
  const created = !container;
  if (created) document.body.appendChild(target);

  // Lazy-require to support UMD script-tag usage
  const ReactDOM = require('react-dom');
  let root: { render: (el: React.ReactElement) => void; unmount: () => void } | null = null;
  const reactParts = (React.version || '').split('.');
  const reactMajor = Number.parseInt(reactParts[0], 10);
  const reactMinor = Number.parseInt(reactParts[1], 10);
  const reactSupportsCreateRoot =
    Number.isFinite(reactMajor) &&
    (reactMajor > 18 || (reactMajor === 18 && (Number.isFinite(reactMinor) ? reactMinor >= 0 : false)));

  if (reactSupportsCreateRoot) {
    try {
      const ReactDOMClient = require('react-dom/client');
      if (ReactDOMClient?.createRoot) {
        try {
          root = ReactDOMClient.createRoot(target);
        } catch (_err) {
          root = null;
        }
      }
    } catch (_err) {
      // Fall back to legacy render
    }
  }

  // Lazy import ChatWidget to avoid circular dependency at module level
  const { default: ChatWidget } = require('./ChatWidget');

  const noop = () => {};
  const rejectNotReady = () => Promise.reject(new Error('Widget not mounted yet'));
  const api: MountAPI = {
    open: noop,
    close: noop,
    toggle: noop,
    unmount: noop,
    createThread: rejectNotReady as MountAPI['createThread'],
    updateThread: rejectNotReady as MountAPI['updateThread'],
    deleteThread: rejectNotReady as MountAPI['deleteThread'],
    createMessage: rejectNotReady as MountAPI['createMessage'],
    fetchThread: rejectNotReady as MountAPI['fetchThread'],
    getSessionId: () => null,
    getThreadId: () => null,
    trackOutcome: noop as MountAPI['trackOutcome'],
  };

  type WrapperProps = ChatWidgetProps & { defaultOpen?: boolean; open?: boolean };

  const Wrapper: React.FC<WrapperProps> = p => {
    const [open, setOpen] = React.useState(!!p.defaultOpen || !!p.open);
    const ref = React.useRef<ChatWidgetAPI>(null);

    React.useEffect(() => {
      if (typeof p.open === 'boolean') {
        setOpen(p.open);
      }
    }, [p.open]);

    api.open = () => setOpen(true);
    api.close = () => setOpen(false);
    api.toggle = () => setOpen(v => !v);
    api.createThread = args =>
      ref.current
        ? ref.current.createThread(args)
        : Promise.reject(new Error('Widget not mounted'));
    api.updateThread = (id, patch) =>
      ref.current
        ? ref.current.updateThread(id, patch)
        : Promise.reject(new Error('Widget not mounted'));
    api.deleteThread = id =>
      ref.current ? ref.current.deleteThread(id) : Promise.reject(new Error('Widget not mounted'));
    api.createMessage = args =>
      ref.current
        ? ref.current.createMessage(args)
        : Promise.reject(new Error('Widget not mounted'));
    api.fetchThread = () =>
      ref.current ? ref.current.fetchThread() : Promise.reject(new Error('Widget not mounted'));
    api.getSessionId = () => ref.current?.getSessionId?.() ?? null;
    api.getThreadId = () => ref.current?.getThreadId?.() ?? null;
    api.trackOutcome = outcome => ref.current?.trackOutcome?.(outcome);

    return React.createElement(ChatWidget, { ...p, open, onOpenChange: setOpen, ref });
  };

  const element = React.createElement(Wrapper, props);
  if (root) {
    root.render(element);
    api.unmount = () => {
      root!.unmount();
      if (created && target.parentNode) target.parentNode.removeChild(target);
    };
  } else {
    // Legacy React <18 fallback for broad compat
    // eslint-disable-next-line react/no-deprecated
    ReactDOM.render(element, target);
    api.unmount = () => {
      // eslint-disable-next-line react/no-deprecated
      ReactDOM.unmountComponentAtNode(target);
      if (created && target.parentNode) target.parentNode.removeChild(target);
    };
  }

  return api;
}
